import { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import {
  FieldGroup,
  FieldGroupHeader,
  FieldRow,
  FieldSkeleton,
  FieldsTable,
  FieldValue,
} from '@/components/layout/field-row'
import { TabCard } from '@/components/layout/tab-card'
import { Badge } from '@/components/ui/badge'
import { Typography } from '@/components/ui/typography'

import * as S from './provider-card.styled'

const DEFAULT_SKELETON_GROUPS: SkeletonGroup[] = [{ labels: [] }]

export interface SkeletonGroup {
  labels: string[]
  title?: string
}

interface ProviderCardProps {
  actions?: ReactNode
  children?: ReactNode
  configured: boolean
  loading: boolean
  notes?: string[]
  // Note: the status row is added to the first group automatically
  skeletonGroups?: SkeletonGroup[]
  tabs: ReactNode
}

export const ProviderCard = ({
  actions,
  children,
  configured,
  loading,
  notes = [],
  skeletonGroups = DEFAULT_SKELETON_GROUPS,
  tabs,
}: ProviderCardProps) => {
  const { t } = useTranslation(['organisation'])

  return (
    <TabCard
      actions={loading ? undefined : actions}
      title={t('organisation:places.accessControl.credentials.title')}
    >
      {tabs}
      {loading ? (
        <FieldsTable>
          {skeletonGroups.map(({ labels, title }, groupIndex) => (
            <FieldGroup key={groupIndex}>
              {title && <FieldGroupHeader>{title}</FieldGroupHeader>}
              {(groupIndex === 0
                ? [t('organisation:places.integrations.keyless.status.label'), ...labels]
                : labels
              ).map((label) => (
                <FieldRow key={label} label={label}>
                  <FieldValue $isViewOnly>
                    <FieldSkeleton height="1.6rem" width="20rem" />
                  </FieldValue>
                </FieldRow>
              ))}
            </FieldGroup>
          ))}
        </FieldsTable>
      ) : (
        <>
          <FieldsTable>
            <FieldRow label={t('organisation:places.integrations.keyless.status.label')}>
              <FieldValue>
                <Badge color={configured ? 'green' : 'neutral'} size="small" variant="light">
                  {configured
                    ? t('organisation:places.integrations.keyless.status.connected')
                    : t('organisation:places.integrations.keyless.status.notConfigured')}
                </Badge>
              </FieldValue>
            </FieldRow>
            {children}
          </FieldsTable>
          {notes.length > 0 && (
            <S.Notes>
              {notes.map((note) => (
                <Typography key={note} size="text-small" color="black500">
                  {note}
                </Typography>
              ))}
            </S.Notes>
          )}
        </>
      )}
    </TabCard>
  )
}
